/**
 * Tier change mutations: upgrade (immediate) and downgrade (scheduled at period end).
 * Plan: billing_infrastructure_plan §1.4, Phase C.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';
import type { AccountTier } from '@/contexts/AppContext';

const BILLING_PREVIEW_KEY = 'billing-preview';

export function useTierChange() {
  const queryClient = useQueryClient();
  const { organization, refreshData } = useApp();
  const orgId = organization?.id ?? null;

  const upgradeTier = useMutation({
    mutationFn: async ({
      plantId,
      fromTier,
      toTier,
    }: {
      plantId: string;
      fromTier: AccountTier;
      toTier: AccountTier;
    }) => {
      if (!orgId) throw new Error('No organization');
      const { error } = await supabase
        .from('plants')
        .update({ tier: toTier })
        .eq('id', plantId);
      if (error) throw error;
      await invokeAuthedFunction(supabase, 'billing-sync', { org_id: orgId });
      return { plantId, toTier };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLING_PREVIEW_KEY] });
      void refreshData();
    },
  });

  const downgradeTier = useMutation({
    mutationFn: async ({
      plantId,
      fromTier,
      toTier,
      effectiveAt,
    }: {
      plantId: string;
      fromTier: AccountTier;
      toTier: AccountTier;
      effectiveAt: string;
    }) => {
      const { error } = await supabase
        .from('plants')
        .update({
          tier_pending: toTier,
          tier_effective_at: effectiveAt,
        })
        .eq('id', plantId);
      if (error) throw error;
      return { plantId, toTier, effectiveAt };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLING_PREVIEW_KEY] });
      void refreshData();
    },
  });

  return { upgradeTier, downgradeTier };
}
